import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  List, X, MagnifyingGlass, ShoppingCart, Heart, User, Sun, Moon, CaretDown, Lightning, WhatsappLogo, EnvelopeSimple,
} from "@phosphor-icons/react";
import { NAV, SITE } from "@/data/site";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeProvider";
import { useSettings } from "@/context/SettingsContext";
import { useCurrency } from "@/context/CurrencyContext";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const { count, setOpen } = useCart();
  const wishlist = useWishlist();
  const { user, isAdmin } = useAuth();
  const { theme, toggle } = useTheme();
  const { settings } = useSettings();
  const { currency, setCurrency } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setSearchOpen(false); }, [location.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/shop?search=${encodeURIComponent(q.trim())}`); setSearchOpen(false); setQ(""); }
  };

  const gbpSel = (
    <select
      value={currency}
      onChange={(e) => setCurrency(e.target.value)}
      className="h-9 cursor-pointer rounded-full border border-border bg-background px-2 text-xs font-bold outline-none transition-colors hover:border-primary"
      aria-label="Select currency"
      data-testid="currency-selector"
    >
      <option value="GBP">£ GBP</option>
      <option value="EUR">€ EUR</option>
      <option value="USD">$ USD</option>
    </select>
  );

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-primary text-primary-foreground text-center text-xs sm:text-sm font-medium py-2 px-4">
        <span className="inline-flex items-center gap-2">
          <Lightning size={14} weight="fill" /> {settings.announcement || "Free UK delivery & 0% finance available · Europe-wide shipping"}
        </span>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "glass shadow-soft" : "bg-background border-b border-border"
        }`}
        data-testid="site-header"
      >
        <div className="container-px">
          <div className="flex h-[68px] items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="Ebikes&Gear logo" className="h-9 w-auto max-w-[180px] object-contain" />
              ) : (
                <>
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground font-heading font-black text-lg">E</span>
                  <span className="font-heading text-xl font-black tracking-tight leading-none hidden sm:block">
                    Ebikes<span className="text-primary">&</span>Gear
                  </span>
                </>
              )}
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
              {NAV.map((item) => (
                <div key={item.label} className="relative group">
                  <Link
                    to={item.to}
                    className="flex items-center gap-1 text-sm font-semibold py-2 link-underline"
                  >
                    {item.label}
                    {item.children && <CaretDown size={12} className="transition-transform group-hover:rotate-180" />}
                  </Link>
                  {item.children && (
                    <div className="absolute left-1/2 top-full -translate-x-1/2 pt-3 opacity-0 invisible translate-y-2 transition-all duration-200 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0">
                      <div className="w-56 rounded-2xl border border-border bg-popover p-2 shadow-soft-lg">
                        {item.children.map((c) => (
                          <Link
                            key={c.to + c.label}
                            to={c.to}
                            className="block rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-muted hover:text-primary transition-colors"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              {gbpSel}
              <a href={SITE.whatsappHref} target="_blank" rel="noreferrer" className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition-opacity hover:opacity-90 active:scale-95" data-testid="header-whatsapp-btn">
                <WhatsappLogo size={15} weight="fill" /> WhatsApp Us
              </a>
              <button onClick={() => setSearchOpen((s) => !s)} className="grid h-10 w-10 place-items-center rounded-full hover:bg-muted transition-colors" aria-label="Search" data-testid="search-toggle">
                <MagnifyingGlass size={20} />
              </button>
              <button onClick={toggle} className="grid h-10 w-10 place-items-center rounded-full hover:bg-muted transition-colors" aria-label="Toggle theme" data-testid="theme-toggle">
                {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <Link to="/wishlist" className="relative grid h-10 w-10 place-items-center rounded-full hover:bg-muted transition-colors" aria-label="Wishlist" data-testid="wishlist-link">
                <Heart size={20} />
                {wishlist.count > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 grid h-4 w-4 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">{wishlist.count}</span>
                )}
              </Link>
              <Link to={user ? (isAdmin ? "/admin" : "/account") : "/login"} className="grid h-10 w-10 place-items-center rounded-full hover:bg-muted transition-colors" aria-label="Account" data-testid="account-link">
                <User size={20} />
              </Link>
              <button onClick={() => setOpen(true)} className="relative grid h-10 w-10 place-items-center rounded-full hover:bg-muted transition-colors" aria-label="Cart" data-testid="cart-toggle">
                <ShoppingCart size={20} />
                {count > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 grid h-4 w-4 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground" data-testid="cart-count">{count}</span>
                )}
              </button>
              <button onClick={() => setMobileOpen(true)} className="grid h-10 w-10 place-items-center rounded-full hover:bg-muted transition-colors lg:hidden" aria-label="Menu" data-testid="mobile-menu-toggle">
                <List size={22} />
              </button>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <form onSubmit={submitSearch} className="pb-4 pt-1" data-testid="search-form">
              <div className="flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3">
                <MagnifyingGlass size={20} className="text-muted-foreground" />
                <input
                  autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search bikes, accessories, brands…"
                  className="flex-1 bg-transparent outline-none text-sm"
                  data-testid="search-input"
                />
                <button type="submit" className="btn-primary !px-5 !py-2 text-sm">Search</button>
              </div>
            </form>
          )}
        </div>
      </header>

      {/* Mobile menu */}
      <div className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-300 ${mobileOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-[85%] max-w-sm bg-background p-6 overflow-y-auto transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "translate-x-full"}`} data-testid="mobile-menu">
          <div className="flex items-center justify-between mb-8">
            <span className="font-heading text-lg font-black">Menu</span>
            <button onClick={() => setMobileOpen(false)} className="grid h-10 w-10 place-items-center rounded-full hover:bg-muted" aria-label="Close menu"><X size={22} /></button>
          </div>
          <nav className="flex flex-col gap-1">
            {NAV.map((item) => (
              <div key={item.label}>
                <Link to={item.to} className="block py-3 text-lg font-bold border-b border-border">{item.label}</Link>
                {item.children && (
                  <div className="flex flex-col py-1">
                    {item.children.map((c) => (
                      <Link key={c.to + c.label} to={c.to} className="py-2 pl-4 text-sm text-muted-foreground hover:text-primary">{c.label}</Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
          <div className="mt-8 flex flex-col gap-3">
            <a href={SITE.whatsappHref} target="_blank" rel="noreferrer" className="btn-primary w-full" data-testid="mobile-menu-whatsapp"><WhatsappLogo size={18} weight="fill" /> WhatsApp Us</a>
            <a href={`mailto:${settings.contact?.email || SITE.email}`} className="btn-outline w-full" data-testid="mobile-menu-email"><EnvelopeSimple size={18} /> Email Us</a>
            <Link to={user ? "/account" : "/login"} className="btn-primary w-full">{user ? "My Account" : "Sign In"}</Link>
          </div>
        </div>
      </div>
    </>
  );
}
